/**
 * AI assistant — floating chat widget rendered by {@link ../../app.component.ts}
 * across every route.
 *
 * **What it does:** sends every user message to the backend's
 * `/api/v1/assistant/chat` endpoint, which proxies to the Anthropic
 * Messages API (Claude Haiku 4.5 by default). The Anthropic key stays
 * server-side. Multi-turn conversation history is maintained in
 * component state and sent with every request — the backend is
 * stateless.
 *
 * **Graceful degradation:** the assistant works without an LLM. When
 * the backend returns 503 (missing `ANTHROPIC_API_KEY`, upstream rate
 * limit, or any other upstream failure) the component silently falls
 * back to the built-in keyword bot below. The visitor sees a sensible
 * answer either way; they never see an error message.
 *
 * **Transcript cap:** to bound latency and token cost, the component
 * sends at most the most recent `HISTORY_CAP` turns (excluding the
 * always-pinned greeting). The greeting itself stays local — it never
 * makes it into the request.
 */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AssistantService, type AssistantMessage } from '../../services/assistant.service';

/** Max number of (user + assistant) turns sent to the backend on each call. */
const HISTORY_CAP = 16;

interface ChatMessage {
  from: 'bot' | 'user';
  text: string;
}

@Component({
  selector: 'app-ai-assistant',
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-assistant.component.html',
  styleUrls: ['./ai-assistant.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantComponent {
  chatMinimized = true;
  chatDismissed = false;
  chatInput = '';
  chatTyping = false;
  chatMessages: ChatMessage[] = [
    {
      from: 'bot',
      text: "Hi there! I'm the EntropyGroupLimited assistant. How can I help you today?",
    },
  ];

  private assistant = inject(AssistantService);
  private cdr = inject(ChangeDetectorRef);

  /**
   * Keyword → reply table used as the offline fallback when the
   * backend is unreachable or unconfigured. Same content as before
   * the LLM integration; the bot is no worse than it used to be when
   * the API key is missing.
   */
  private readonly botResponses: Record<string, string> = {
    sales:
      'For sales inquiries, visit our Contact page and click "Contact us" under Sales support, or email businessdevelopment@entropygrouplimited.com.',
    pricing:
      'Our pricing depends on the engagement type — staff augmentation, project-based, or reseller. Visit our Contact page to discuss specifics.',
    bug: 'To report a bug, head to our Contact page and click "Report a bug" to fill out the form with details about the issue.',
    support:
      'For technical support, visit our Contact page and click "Get support" or email businessdevelopment@entropygrouplimited.com.',
    service:
      'We offer IT consulting in Data Science, Cybersecurity, Custom Software Development, and Enterprise Infrastructure.',
    location:
      'Our physical office is in Minneapolis, MN (326 E Hennepin Ave, #206). Our mailing address is in Saint Paul, MN (2136 Ford Pkwy #9033).',
    phone: 'You can reach us by phone at +1 (612) 555-1234, Mon–Fri from 8am to 5pm.',
    email:
      'You can email us at businessdevelopment@entropygrouplimited.com for general inquiries or technical help.',
    hello: 'Hello! How can I assist you today?',
    hi: 'Hi! What can I help you with?',
    thanks: "You're welcome! Let me know if there's anything else I can help with.",
    bye: 'Goodbye! Feel free to come back anytime you have questions.',
  };

  toggleChat(): void {
    this.chatMinimized = !this.chatMinimized;
  }

  dismissChat(): void {
    this.chatDismissed = true;
  }

  sendMessage(): void {
    const text = this.chatInput.trim();
    if (!text) return;

    this.chatMessages.push({ from: 'user', text });
    this.chatInput = '';
    this.chatTyping = true;

    const history = this.buildHistoryForRequest(text);

    this.assistant.chat(history).subscribe({
      next: (reply) => this.completeReply(reply.trim() || this.keywordReply(text)),
      error: () => this.completeReply(this.keywordReply(text)),
    });
  }

  /**
   * Build the request transcript from the component's chat log. The
   * first message (the bot greeting) is dropped, and the tail is
   * capped to `HISTORY_CAP` entries so old conversations don't bloat
   * the request payload.
   *
   * @param   userText - The user's latest input (already appended to
   *                     `chatMessages` by the caller — included here
   *                     for clarity; not needed for slicing).
   * @returns Array suitable for the backend's `AssistantChatDto.history`.
   */
  private buildHistoryForRequest(_userText: string): AssistantMessage[] {
    const turns = this.chatMessages.slice(1).map((m) => ({
      role: (m.from === 'bot' ? 'assistant' : 'user') as 'assistant' | 'user',
      content: m.text,
    }));
    return turns.length > HISTORY_CAP ? turns.slice(turns.length - HISTORY_CAP) : turns;
  }

  /**
   * Finalise a reply: clear the typing indicator, append the message,
   * and notify the OnPush change detector (async observable callbacks
   * outside the Zone don't auto-trigger CD).
   */
  private completeReply(text: string): void {
    this.chatTyping = false;
    this.chatMessages = [...this.chatMessages, { from: 'bot', text }];
    this.cdr.markForCheck();
  }

  /**
   * Match the user input against the keyword table; default to the
   * generic "contact page" fallback when no keyword matches. Used
   * both as the offline fallback and as the response when the LLM
   * returns an empty body.
   */
  private keywordReply(input: string): string {
    const lower = input.toLowerCase();
    for (const [keyword, response] of Object.entries(this.botResponses)) {
      if (lower.includes(keyword)) return response;
    }
    return 'Thanks for your message! For detailed help, please visit our Contact page. Our team will get back to you within 1–2 business days.';
  }
}
